import { Stack, Spinner, MessageBar, MessageBarType, MessageBarButton, Text, getTheme } from '@fluentui/react';
import React from 'react';
import { Case, QueueConfiguration, Feedback, getCaseFeedback, trackGmcException } from 'services';
import { EvidenceHeader, EvidenceBody } from './components';

const theme = getTheme();

const styleEvidenceWrapper: React.CSSProperties = {
  width: '80%',
  height: 'auto'
};

const fullHeight: React.CSSProperties = {
  maxHeight: '60vh',
  height: '60vh'
};

const styleEvidenceContainer = {
  root: {
    borderColor: theme.palette.neutralDark
  }
};

const styleLoadingSpinner = {
  root: {
    height: '100%'
  },
  circle: {
    width: '72px',
    height: '72px'
  }
};

export interface EvidenceProps {
  selectedCases: Case[];
  config: QueueConfiguration;
  isCollapsed: boolean;
  onCancelClick: () => void;
}

interface EvidenceState {
  isCollapsed: boolean;
  isLoading: boolean;
  error: boolean;
  feedbacks: Feedback[];
}

export class Evidence extends React.PureComponent<EvidenceProps, EvidenceState> {
  public state = {
    isCollapsed: this.props.isCollapsed,
    error: false,
    isLoading: true,
    feedbacks: []
  };

  async componentDidMount(): Promise<void> {
    await this.tryGetFeedback();
  }

  async componentDidUpdate(prevProps: EvidenceProps): Promise<void> {
    if (
      this.props.selectedCases.length === 1 &&
      (prevProps.selectedCases.length !== 1 || this.props.selectedCases[0].id !== prevProps.selectedCases[0].id)
    ) {
      await this.tryGetFeedback();
    }

    if (!this.state.isCollapsed && this.props.selectedCases.length !== 1) {
      this.setState({
        isCollapsed: true
      });
    }
  }

  render(): JSX.Element {
    // Wrapper surrounds the entire evidence panel
    // Container holds everything under the title
    // If nothing is selected we reset the collapse or if multiple cases are selected
    return (
      <div style={{ display: this.props.selectedCases.length > 0 ? 'block' : 'none', ...styleEvidenceWrapper }}>
        {!this.state.isCollapsed && (
          <div className="ms-motion-slideUpIn $ms-motion-duration-4">
            <Stack style={fullHeight}>
              <Stack.Item>
                <EvidenceHeader
                  onToggleClick={(): void => this.toggleHistory()}
                  onCancelClick={this.props.onCancelClick}
                  selectedCases={this.props.selectedCases}
                  config={this.props.config}
                  isCollapsed={this.state.isCollapsed}
                  feedbacks={this.state.feedbacks}
                />
              </Stack.Item>
              {this.state.error && (
                <Stack.Item grow className="ms-bgColor-white p-2 overflow-hidden" styles={styleEvidenceContainer}>
                  <MessageBar
                    messageBarType={MessageBarType.severeWarning}
                    actions={
                      <div>
                        <MessageBarButton onClick={(): void => this.retryGetFeedback()}>Retry</MessageBarButton>
                      </div>
                    }
                  >
                    <Text variant="mediumPlus">There was an error while loading the feedback for this case.</Text>
                  </MessageBar>
                </Stack.Item>
              )}
              {this.state.isLoading && !this.state.error && (
                <Stack.Item grow className="ms-bgColor-white overflow-hidden" styles={styleEvidenceContainer}>
                  <Spinner styles={styleLoadingSpinner} label="Loading" />
                </Stack.Item>
              )}
              {!this.state.isLoading && !this.state.error && this.props.selectedCases.length === 1 && (
                <Stack.Item grow className="ms-bgColor-white overflow-hidden" styles={styleEvidenceContainer}>
                  <EvidenceBody selectedCase={this.props.selectedCases[0]} feedbacks={this.state.feedbacks} />
                </Stack.Item>
              )}
            </Stack>
          </div>
        )}
        {this.state.isCollapsed && (
          <div className="ms-motion-slideDownIn $ms-motion-duration-4">
            <EvidenceHeader
              onToggleClick={(): void => this.toggleHistory()}
              onCancelClick={this.props.onCancelClick}
              selectedCases={this.props.selectedCases}
              config={this.props.config}
              isCollapsed={this.state.isCollapsed}
              feedbacks={this.state.feedbacks}
            />
          </div>
        )}
      </div>
    );
  }

  private retryGetFeedback(): void {
    this.setState({
      isLoading: true,
      error: false
    });
    // TODO: see how this is working
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.tryGetFeedback();
  }

  private toggleHistory(): void {
    this.setState({ isCollapsed: !this.state.isCollapsed });
  }

  private async tryGetFeedback(): Promise<void> {
    try {
      if (this.props.selectedCases.length === 1) {
        const selectedCase: Case = this.props.selectedCases[0];
        const feedbacks: Feedback[] = await getCaseFeedback(
          selectedCase.tenant.tenantId,
          selectedCase.title.titleId,
          selectedCase.subject.subjectType,
          selectedCase.subject.subjectId,
          selectedCase.id
        );

        this.setState({
          isLoading: false,
          feedbacks
        });
      }
    } catch (error) {
      this.setState({
        error: true,
        isLoading: false
      });
      trackGmcException('Failed to fetch case feedback: case=' + JSON.stringify(this.props.selectedCases));
    }
  }
}
